import { useState } from "react";
import type { UserPersonalDetails } from "roo-shared";
import { RooDate } from "roo-time";
import { logout } from "../services/session.service.js";
import { DeleteAccountDialog } from "../components/DeleteAccountDialog.comp.js";

/**
 * The walking skeleton's terminus: proof that login worked, the session restored, and an
 * authenticated call returned real user data.
 *
 * It renders everything `getUserPersonalDetails` returns rather than a curated subset — at this
 * stage the screen's job is to demonstrate the round trip, and seeing the whole payload is more
 * useful for that than a designed profile card would be. The identifiers at the bottom are the ones
 * support actually asks for. Product surfaces (transactions, accounts, reports, Payback) replace
 * this later; it is not a profile page.
 *
 * Deliberately NOT shown: `profileImageBase64` (an avatar is design work this slice doesn't need)
 * and `isEmailEditDisabled` (a capability flag, not a detail about the person).
 */
export function SignedInPage(p: { user: UserPersonalDetails }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const displayName = [p.user.firstName, p.user.lastName].filter(Boolean).join(" ").trim();

  // `createdAt` arrives as a RooDate (decoded from the wire's date placeholder). Rendered in the
  // user's OWN communication timezone rather than the browser's, so it matches what the mobile app
  // would show them — the repo's timezone-is-explicit-data rule, applied at a display site.
  const memberSince = formatDate(p.user.createdAt, p.user.communicationTimezone, p.user.communicationLocale);

  return (
    <main className="card" data-testid="signed-in-page">
      <h1 className="logo">roo</h1>
      <h2>You're signed in</h2>
      <p className="hint">Your account, as the server sees it.</p>

      <dl className="details">
        <dt>Name</dt>
        <dd data-testid="signed-in-name">{displayName || "—"}</dd>

        <dt>Email</dt>
        <dd data-testid="signed-in-email">{p.user.email}</dd>

        <dt>Member since</dt>
        <dd data-testid="signed-in-created">{memberSince}</dd>

        <dt>Timezone</dt>
        <dd data-testid="signed-in-timezone">{p.user.communicationTimezone}</dd>

        <dt>Locale</dt>
        <dd data-testid="signed-in-locale">{p.user.communicationLocale}</dd>
      </dl>

      <dl className="details details-mono">
        <dt>User ID</dt>
        <dd data-testid="signed-in-user-id">{p.user.userId}</dd>

        <dt>Budget ID</dt>
        <dd data-testid="signed-in-budget-id">{p.user.selectedBudgetId}</dd>
      </dl>

      <button type="button" data-testid="logout-button" onClick={() => void logout()}>
        Log out
      </button>
      {/* Destructive, so it's visually demoted and sits below Log Out — the same ordering mobile's
          Settings uses, so the dangerous action is never where the routine one is expected. */}
      <button
        type="button"
        className="link danger-link"
        data-testid="delete-account-button"
        onClick={() => setIsDeleting(true)}
      >
        Delete account
      </button>

      {isDeleting ? <DeleteAccountDialog onClose={() => setIsDeleting(false)} /> : null}
    </main>
  );
}

/**
 * Never let a malformed date break the screen that exists to prove the round trip worked.
 *
 * Both the timezone AND the locale come from the user's own record — the same explicit-data
 * discipline, applied to both halves of the format call. Hardcoding `en-US` here would render
 * "December 31, 2023" directly above a row reading "Locale: de-DE".
 */
function formatDate(value: unknown, timeZone: string, locale: string): string {
  // `instanceof Date` is the sanctioned form even here — the repo's lint rule calls it out
  // explicitly, because getting the prototype right when extending Date is finicky. Construction
  // still goes through RooDate.
  const date = value instanceof Date ? value : new RooDate(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  const options = { year: "numeric", month: "long", day: "numeric" } as const;
  try {
    return date.toLocaleDateString(locale, { ...options, timeZone });
  } catch {
    // An unrecognised locale or timezone shouldn't blank the field.
    return date.toLocaleDateString("en-US", options);
  }
}
